import { Fragment } from 'react';
import { DEFAULT_THEME_NAME, useChartContext } from '@shopify/polaris-viz-core';
import { hasHiddenComparisonSeries } from '../../utilities/hasHiddenComparisonSeries.js';
import { useGetLongestLabelFromData } from './hooks/useGetLongestLabelFromData.js';
import { SPACE_BETWEEN_LABEL_AND_VALUE } from './constants.js';
import { getTooltipContentTemplateColumnCount } from './utilities/get-tooltip-template-content-column-count.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { TooltipContentContainer } from './components/TooltipContentContainer/TooltipContentContainer.js';
import { TooltipTitle } from './components/TooltipTitle/TooltipTitle.js';
import { TooltipSeries } from './components/TooltipSeries/TooltipSeries.js';
import { TooltipRow } from './components/TooltipRow/TooltipRow.js';

const FONT_SIZE_OFFSET = 1.061;
const PREVIEW_WIDTH = 14;
function TooltipContent({
  data,
  theme = DEFAULT_THEME_NAME,
  title,
  ignoreColorVisionEvents = false
}) {
  var _data$;
  const {
    keyWidth,
    valueWidth
  } = useGetLongestLabelFromData(data);
  const {
    comparisonSeriesIndexes
  } = useChartContext();
  const leftWidth = keyWidth * FONT_SIZE_OFFSET;
  const rightWidth = valueWidth * FONT_SIZE_OFFSET;
  const isMultiSeries = ((_data$ = data[0]) === null || _data$ === void 0 ? void 0 : _data$.data.length) > 2;
  return /*#__PURE__*/jsx(TooltipContentContainer, {
    maxWidth: PREVIEW_WIDTH + leftWidth + SPACE_BETWEEN_LABEL_AND_VALUE + rightWidth,
    theme: theme,
    ignoreColorVisionEvents: ignoreColorVisionEvents,
    children: ({
      activeColorVisionIndex
    }) => /*#__PURE__*/jsxs(Fragment, {
      children: [title != null && /*#__PURE__*/jsx(TooltipTitle, {
        theme: theme,
        children: title
      }), data.map((tooltipData, dataIndex) => {
        const {
          data: series,
          name,
          shape
        } = tooltipData;
        const hasName = name != null;
        const isEmpty = !hasName && series.length === 0;
        if (isEmpty) {
          return null;
        }
        return /*#__PURE__*/jsx(TooltipSeries, {
          name: name,
          templateColumnCount: getTooltipContentTemplateColumnCount(tooltipData),
          children: series.map(({
            key,
            value,
            color,
            isComparison,
            isHidden,
            trend
          }, seriesIndex) => {
            // This check is for when we are rendering multiple series lines
            // and we have an active index.
            // We only want to render the active series line and it's
            // matching comparison series line.
            if (!isHidden &&
            // If we more than 2 series lines...
            isMultiSeries &&
            // and the series is not a comparison series...
            isComparison !== true &&
            // and we have an active index...
            activeColorVisionIndex !== -1 &&
            // and the series index is not the active index, hide it.
            seriesIndex !== activeColorVisionIndex) {
              return null;
            }
            if (hasHiddenComparisonSeries({
              index: seriesIndex,
              activeColorVisionIndex,
              comparisonSeriesIndexes
            })) {
              return null;
            }
            return /*#__PURE__*/jsx(TooltipRow, {
              color: color,
              isComparison: isComparison,
              label: key,
              shape: shape,
              trend: trend,
              value: value
            }, `row-${seriesIndex}`);
          })
        }, dataIndex);
      })]
    })
  });
}

export { TooltipContent };
